import { getSnapshot } from '@configs/axios/session-expired-store/sessionExpiredStore';
import { useMsalAuth } from '@contexts/msal-provider';
import { getCurrentDeepLinkUrl } from '@shared/helpers/deep-link/getCurrentDeepLinkUrl';
import { getMsalInstance } from '@shared/helpers/msal-instance/msalInstance';
import { useCallback, useState } from 'react';

import type { UseReauthenticateResult } from './useReauthenticate.types';

/**
 * Returns the "Go to login" action for an expired session: sends the user back to
 * their identity provider (prefilled login hint) and restores the current page after login.
 */
export function useReauthenticate(): UseReauthenticateResult {
  const { login } = useMsalAuth();
  const [hasFailed, setHasFailed] = useState(false);

  const reauthenticate = useCallback(async (): Promise<void> => {
    const expiredSession = getSnapshot();
    if (!expiredSession) {
      return;
    }

    try {
      const msal = await getMsalInstance();
      const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
      if (account) {
        await msal.clearCache({ account }).catch((error: unknown) => {
          console.error('MSAL clearCache failed:', error);
        });
      }
      await login(
        expiredSession.authType,
        getCurrentDeepLinkUrl(),
        expiredSession.loginHint
      );
    } catch (error) {
      console.error('MSAL reauthentication failed:', error);
      setHasFailed(true);
    }
  }, [login]);

  return { reauthenticate, hasFailed };
}
